import { t } from '@lingui/macro'
import { useCallback, useState } from 'react'
import { CioxRequestCreateDTO } from '../client/models/CioxRequestCreateDTO'
import { CioxRequestDTO } from '../client/models/CioxRequestDTO'
import { CioxRequestUpdateDTO } from '../client/models/CioxRequestUpdateDTO'
import { AspenCioxRequestsService } from '../client/services/AspenCioxRequestsService'

export interface CioxRequestData {
    providerName: string
    status?: string
}

interface UseCioxRequestsProps {
    patientId: string
}

interface UseCioxRequestsReturn {
    cioxRequests: CioxRequestDTO[]
    isLoading: boolean
    error: string | null
    fetchCioxRequests: () => Promise<void>
    createCioxRequest: (data: CioxRequestData) => Promise<void>
    updateCioxRequest: (requestId: string, data: Partial<CioxRequestData>) => Promise<void>
    deleteCioxRequest: (requestId: string) => Promise<void>
}

export const useCioxRequests = ({ patientId }: UseCioxRequestsProps): UseCioxRequestsReturn => {
    const [cioxRequests, setCioxRequests] = useState<CioxRequestDTO[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchCioxRequests = useCallback(async () => {
        if (!patientId) return

        setIsLoading(true)
        setError(null)
        try {
            // Scoped server-side. Fetching every request in the organization and filtering
            // here silently dropped rows once an org outgrew the page size.
            const response = await AspenCioxRequestsService.indexCioxRequestsGet({
                page: 1,
                limit: 100,
                patientId
            })

            setCioxRequests(response.data)
        } catch (err) {
            setError(t`Failed to fetch ciox requests. Please try again.`)
            console.error(t`Failed to fetch ciox requests:`, err)
        } finally {
            setIsLoading(false)
        }
    }, [patientId])

    const createCioxRequest = useCallback(
        async (data: CioxRequestData) => {
            if (!patientId) return

            setIsLoading(true)
            setError(null)

            try {
                const requestBody: CioxRequestCreateDTO = {
                    providerName: data.providerName,
                    status: data.status || 'NONE',
                    patientId: patientId
                }

                const response = await AspenCioxRequestsService.createCioxRequestsPost({
                    requestBody
                })

                setCioxRequests((prevRequests) => [response, ...prevRequests])
            } catch (err) {
                setError(t`Failed to create ciox request. Please try again.`)
                console.error(t`Failed to create ciox request:`, err)
                throw err // Re-throw to let the component handle it
            } finally {
                setIsLoading(false)
            }
        },
        [patientId]
    )

    const updateCioxRequest = useCallback(async (requestId: string, data: Partial<CioxRequestData>) => {
        setIsLoading(true)
        setError(null)

        try {
            const requestBody: CioxRequestUpdateDTO = {
                providerName: data.providerName || null,
                status: data.status || null,
                patientId: null // Don't update patient ID
            }

            const response = await AspenCioxRequestsService.updateCioxRequestsCioxRequestIdPatch({
                cioxRequestId: requestId,
                requestBody
            })

            setCioxRequests((prevRequests) => prevRequests.map((req) => (req.id === requestId ? response : req)))
        } catch (err) {
            setError(t`Failed to update ciox request. Please try again.`)
            console.error(t`Failed to update ciox request:`, err)
            throw err // Re-throw to let the component handle it
        } finally {
            setIsLoading(false)
        }
    }, [])

    const deleteCioxRequest = useCallback(async (requestId: string) => {
        setIsLoading(true)
        setError(null)

        try {
            await AspenCioxRequestsService.deleteCioxRequestsCioxRequestIdDelete({
                cioxRequestId: requestId
            })

            setCioxRequests((prevRequests) => prevRequests.filter((req) => req.id !== requestId))
        } catch (err) {
            setError(t`Failed to delete ciox request. Please try again.`)
            console.error(t`Failed to delete ciox request:`, err)
            throw err // Re-throw to let the component handle it
        } finally {
            setIsLoading(false)
        }
    }, [])

    return {
        cioxRequests,
        isLoading,
        error,
        fetchCioxRequests,
        createCioxRequest,
        updateCioxRequest,
        deleteCioxRequest
    }
}
